import { useEditorStore } from '~/shell/Store';
import { templateTextFieldsEnabled } from './templateTextFields';

/** Authored text fields a template can show without requiring them. */
export type OptionalTextField = 'subtitle' | 'title' | 'body';

/** For a template whose learner view shows a subtitle, a title and a body. */
export const OPTIONAL_TEXT_FIELDS: OptionalTextField[] = [
  'subtitle',
  'title',
  'body'
];

/**
 * The cue for a short label that groups ideas, names a topic or sets context.
 * Its role comes from the template, even when its title is in a separate
 * control (such as a tab header).
 */
export const KICKER_HINT = 'Add helper tag';

/** A helper tag's name, as its edit panel labels it, once it has text. */
export const KICKER_LABEL = 'Helper tag';

/** What a helper tag is for, word for word as its edit panel says it. */
export const KICKER_HELP =
  'Use a short label to group ideas, name a topic, or set context.';

/** Editor cues name fields by their role, not the stored field name. */
export const OPTIONAL_FIELD_HINTS: Record<OptionalTextField, string> = {
  subtitle: KICKER_HINT,
  title: 'Add title',
  body: 'Add text'
};

/**
 * Whether the editor offers the template text fields here: while the slide is
 * being edited, for a company with the flag (see templateTextFields).
 */
export const offersTemplateTextFields = () =>
  useEditorStore.getState().contentEditable && templateTextFieldsEnabled();

/**
 * The optional fields to offer: only while the slide is being edited, for a
 * company with the template text fields, so what learners, previews and LMS
 * packages receive never changes.
 */
export const optionalFieldsInEditor = (
  fields: OptionalTextField[] | undefined
): OptionalTextField[] =>
  fields?.length && offersTemplateTextFields() ? fields : [];

/**
 * Text values for a field the editor always keeps reachable, under its own
 * name (an answer, a tab label): blank, it shows this cue instead of nothing.
 * Like every cue, only for a company with the template text fields.
 */
export const editorCueValues = (hint: string): { emptyHint?: string } =>
  offersTemplateTextFields() ? { emptyHint: hint } : {};

/**
 * Text values for one field: its cue when it is offered as optional, named
 * for what it is where the field's own name would not say (a quote, a message).
 * A helper tag's cue also brings its name and what it is for.
 */
export const optionalFieldValues = (
  offered: OptionalTextField[],
  field: OptionalTextField,
  hint = OPTIONAL_FIELD_HINTS[field]
): { emptyHint?: string; fieldLabel?: string; fieldHelp?: string } =>
  offered.includes(field)
    ? {
        emptyHint: hint,
        ...(hint === KICKER_HINT && {
          fieldLabel: KICKER_LABEL,
          fieldHelp: KICKER_HELP
        })
      }
    : {};
